import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, Channel, ChannelHealthService, HybridUsageResponse } from 'lib-trend-core';
import { takeUntil } from 'rxjs';

/**
 * Hybrid channel card of the health "Resumo" tab (SPEC-2026-10-whatsapp-hybrid-channel RF-22,
 * RF-25; AC-17, AC-18): the session side's state, the month's outbound split by side and the
 * estimated saving. The saving is an estimate — session messages priced at the business account's
 * service rate, or the platform's reference rate when Meta does not expose one.
 */
@Component({
    selector: 'health-hybrid-card',
    templateUrl: './health-hybrid-card.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HealthHybridCardComponent extends AbstractComponent implements OnInit, OnChanges {

  @Input({ required: true }) channel: Channel;
  @Input() refreshTick: number = 0;

  usage?: HybridUsageResponse;
  failed: boolean = false;

  constructor(
    injector: Injector,
    private readonly channelHealthService: ChannelHealthService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshTick'] && !changes['refreshTick'].firstChange) {
      this.load();
    }
  }

  load(): void {
    this.loadingContent = true;
    this.failed = false;
    this.channelHealthService.getHybridUsage(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (usage) => {
          this.usage = usage;
          this.loadingContent = false;
        },
        error: () => {
          this.failed = true;
          this.loadingContent = false;
        },
      });
  }

  get paired(): boolean {
    return !!this.channel?.hybridConfig?.sessionChannel;
  }

  get connected(): boolean {
    return this.channel?.hybridConfig?.sessionConnected === true;
  }

  get mismatch(): boolean {
    return this.channel?.hybridConfig?.sessionMismatch === true;
  }

  /** The note under the saving: the "reference rate" wording only when Meta gave no account rate. */
  rateNoteKey(): string {
    return this.usage?.rateSource === 'REFERENCE'
      ? 'channel.health.hybrid.rateReferenceNote'
      : 'channel.health.hybrid.rateAccountNote';
  }

  /** Session share of the month's outbound, for the split bar (0–100). */
  sessionPercent(): number {
    const total = (this.usage?.outbound.official ?? 0) + (this.usage?.outbound.session ?? 0);
    return total ? Math.round(((this.usage?.outbound.session ?? 0) / total) * 100) : 0;
  }

  officialPercent(): number {
    const total = (this.usage?.outbound.official ?? 0) + (this.usage?.outbound.session ?? 0);
    return total ? 100 - this.sessionPercent() : 0;
  }
}
